import Slider from 'react-slick';
import { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { BoxProps, Box } from '@mui/material';
import CarouselDots from './CarouselDots';
import CarouselArrows from './CarouselArrows';

export interface CarouselHandle {
    slickNext: () => void;
    slickPrev: () => void;
}

interface CarouselProps extends BoxProps {
    list: React.ReactNode[];
    speed?: number;
    autoplaySpeed?: number;
    showArrows?: boolean;
    autoplay?: boolean;
    onChangeIndex?: (index: number) => void;
}

const Carousel = forwardRef<CarouselHandle, CarouselProps>(
    (
        {
            list,
            speed = 2000,
            autoplaySpeed = 12000,
            showArrows = true,
            autoplay,
            onChangeIndex,
            ...other
        },
        ref
    ) => {
        const theme = useTheme();

        const carouselRef = useRef<Slider>(null);
        const [touched, setTouched] = useState(false);

        const [_currentIndex, setCurrentIndex] = useState(
            theme.direction === 'rtl' ? list.length - 1 : 0
        );

        useImperativeHandle(ref, () => ({
            slickNext: () => carouselRef.current?.slickNext(),
            slickPrev: () => carouselRef.current?.slickPrev(),
        }));

        const settings = {
            speed,
            autoplaySpeed,
            pauseOnHover: true,
            pauseOnFocus: true,
            dots: true,
            arrows: false,
            autoplay: autoplay ?? !touched,
            slidesToShow: 1,
            slidesToScroll: 1,
            rtl: Boolean(theme.direction === 'rtl'),
            beforeChange: (_current: number, next: number) => {
                setCurrentIndex(next);
                onChangeIndex?.(next);
            },
            ...CarouselDots(),
        };

        const handlePrevious = () => {
            if (touched) return;
            setTouched(true);
            carouselRef.current?.slickPrev();
        };

        const handleNext = () => {
            if (touched) return;
            setTouched(true);
            carouselRef.current?.slickNext();
        };

        return (
            <Box
                onMouseDown={() => setTouched(true)}
                {...other}
                sx={{
                    ...other.sx,
                }}
            >
                <Slider ref={carouselRef} {...settings}>
                    {list}
                </Slider>

                {showArrows && list.length > 1 && (
                    <CarouselArrows
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                        spacing={0}
                        sx={{
                            top: 8,
                            right: 16,
                            position: 'absolute',
                            '& .arrow': {
                                p: 0,
                                width: 32,
                                height: 32,
                                opacity: 0.48,
                                color: 'common.white',
                                '&:hover': { color: 'common.white', opacity: 1 },
                            },
                        }}
                    />
                )}
            </Box>
        );
    }
);

export default Carousel;
